import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle, FileText, ArrowRight } from 'lucide-react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { trackToolUsage } from '@/lib/tracking';

const requirements = [
    {
        id: 'cover',
        label: "Cover / Title Page",
        desc: "Must identify the project name, location, and operator info."
    },
    {
        id: 'contacts',
        label: "Contact Information",
        desc: "Project manager, SWPPP preparer, and emergency contacts."
    },
    {
        id: 'site_desc',
        label: "Site & Activity Description",
        desc: "Nature of construction, intended use, and total area to be disturbed."
    },
    {
        id: 'maps',
        label: "Site Maps (Legible)",
        desc: "Must show drainage patterns, slopes, disturbance limits, and BMP locations."
    },
    {
        id: 'sources',
        label: "Potential Pollutant Sources",
        desc: "List of sediment, paints, solvents, fuels, and building materials."
    },
    {
        id: 'controls',
        label: "Description of Controls (BMPs)",
        desc: "Specific erosion and sediment controls for each phase of construction."
    },
    {
        id: 'maintenance',
        label: "Maintenance Procedures",
        desc: "Schedule for inspecting and repairing BMPs (e.g., 'within 24h of rain')."
    },
    {
        id: 'records',
        label: "Inspection Records",
        desc: "Blank forms for logging weekly inspections and corrective actions."
    },
    {
        id: 'certification',
        label: "Signed Certification",
        desc: "Signed statement by the operator acknowledging liability (e.g., 'I certify...')."
    }
];

const SwpppAuditPage: React.FC = () => {
    const [checked, setChecked] = useState<string[]>([]);
    const handleToggle = (id: string) => {
        setChecked(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const progress = Math.round((checked.length / requirements.length) * 100);
    const isComplete = progress === 100;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "SWPPP Self-Audit Checklist",
        "description": "Interactive screening checklist for common construction SWPPP plan elements.",
        "applicationCategory": "BusinessApplication",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
    };

    return (
        <div className="min-h-screen bg-background font-sans selection:bg-blue-500/20">
            <SEO 
                title="SWPPP Plan Review Checklist"
                description="Screen your Stormwater Pollution Prevention Plan for common plan elements, then try a sample field inspection."
                canonical="/tools/swppp-audit"
                jsonLd={jsonLd}
            />

            <div className="bg-muted/30 py-20 border-b border-border">
                <div className="container px-4 text-center max-w-4xl mx-auto">
                    <Breadcrumbs items={[
                        { label: 'Resources', href: '/' },
                        { label: 'SWPPP Auditor', href: '/tools/swppp-audit' }
                    ]} />

                    <div className="inline-flex items-center justify-center p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full mb-6">
                        <FileText size={32} />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                        SWPPP Self-Audit Tool
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        A quick screening checklist based on common elements described in EPA's SWPPP guidance. Always verify your project-specific permit.
                    </p>
                </div>
            </div>

            <div className="container px-4 py-16 max-w-3xl mx-auto">
                <div className="mb-16">
                    <Card className="border-border shadow-lg">
                        <CardHeader className="pb-4">
                            <div className="flex justify-between items-center mb-2">
                                <CardTitle>Plan Requirements</CardTitle>
                                <span className={`font-bold ${isComplete ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {progress}% Ready
                                </span>
                            </div>
                            <Progress value={progress} className="h-2" />
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {requirements.map((req) => (
                                <div key={req.id} className="flex items-start space-x-4 p-3 rounded-lg hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => handleToggle(req.id)}>
                                    <Checkbox 
                                        id={req.id} 
                                        checked={checked.includes(req.id)}
                                        onCheckedChange={() => handleToggle(req.id)}
                                        className="mt-1"
                                    />
                                    <div className="space-y-1">
                                        <label htmlFor={req.id} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                                            {req.label}
                                        </label>
                                        <p className="text-sm text-muted-foreground">
                                            {req.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <div className={`mt-8 p-6 rounded-xl border transition-all duration-500 ${isComplete ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'}`}>
                        <div className="flex gap-4">
                            <div className={`p-3 rounded-full shrink-0 h-fit ${isComplete ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                {isComplete ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">
                                    {isComplete ? "Your plan includes these common elements" : "Items to review"}
                                </h3>
                                <p className="text-sm opacity-90 mb-6 leading-relaxed">
                                    {isComplete 
                                        ? "This screen is a useful starting point, but it is not a compliance determination. RunOffTrack helps you conduct inspections and organize signed records by project."
                                        : "Review unchecked items against the permit and SWPPP requirements that apply to your project. Then see how RunOffTrack records a field inspection."
                                    }
                                </p>
                                <Button size="lg" className={`w-full sm:w-auto ${isComplete ? 'bg-green-600 hover:bg-green-700' : ''}`} asChild>
                                    <Link to="/demo/sample-inspection" onClick={() => trackToolUsage('swppp_audit', 'sample_inspection_clicked', { progress })}>
                                        Try a Sample Inspection <ArrowRight className="ml-2 h-4 w-4"/>
                                    </Link>
                                </Button>
                                <p className="mt-4 text-xs opacity-70">Educational screening only; not legal advice or a substitute for permit review.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEO CONTENT SECTION */}
                <div className="prose dark:prose-invert max-w-none pt-8 border-t border-border">
                    <h2>What makes a SWPPP compliant?</h2>
                    <p>
                        A Stormwater Pollution Prevention Plan (SWPPP) is not just a form you fill out once. It is a living document that must be maintained on-site and updated as construction progresses.
                    </p>
                    <p>
                        According to <strong>EPA Chapter 2 (Developing Your SWPPP)</strong>, the plan must clearly identify all potential sources of pollution and describe the Best Management Practices (BMPs) used to contain them.
                    </p>

                    <h3>Common Audit Red Flags</h3>
                    <ul>
                        <li><strong>Generic Templates:</strong> Using a boilerplate SWPPP that describes BMPs (like silt ponds) that don't actually exist on your specific site.</li>
                        <li><strong>Outdated Maps:</strong> Failure to update the site map to show the current location of stockpiles or concrete washouts.</li>
                        <li><strong>Missing Signatures:</strong> The certification statement must be signed by a "Responsible Corporate Officer" (e.g., VP or President), not just the field superintendent, unless a Delegation of Authority form is signed.</li>
                    </ul>

                    <h3>The "Living Document" Rule</h3>
                    <p>
                        If you change a BMP (e.g., switching from silt fence to fiber rolls), you must update the SWPPP narrative and map within 7 days. Electronic SWPPP management systems like RunOffTrack make this easy by allowing you to update logs digitally without re-printing the entire binder.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SwpppAuditPage;
